import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pencil, Plus, Camera, Sparkles, Trash2, CalendarClock, Users, ChevronRight } from 'lucide-react';
import { useAuth } from '../../../services/authentication/authContext';
import { signOut as supabaseSignOut } from '../../../services/authentication/supabaseService';
import { fetchUserProfile, deleteUser as deleteUserService, updatePriceAlertPreference, updateWeeklyReportsPreference, updateStaffSignupEmailPreference, updateUserProfile, initiateEmailChange } from '../../../services/user/userService';
import type { BackendUser } from '../../../services/user/userService';
import { useTranslation } from 'react-i18next';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import SaveRestaurantModal from '../../restaurant/SaveRestaurantModal';
import DemoAutomationModal from './DemoAutomationModal';
import DemoExtendModal from './DemoExtendModal';
import ClearDataModal from './ClearDataModals';
import ClearDataSelectionModal from './ClearDataSelectionModal';
import ReportSettingsModal from './ReportSettingsModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantLogo from '../../ui/RestaurantLogo';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { Platform } from '../../../types/Platforms';
import { formatPlatformName } from '../../../services/helperService';
import { clearRevenueBySourceIds, clearOrdersBySectionIds } from '../../../services/automation/automationService';
import { canAccessDemoAccount, canAccessStaffPage } from '../../../services/accessManagement/accessManagement';
import { resolveCostTargets } from '../../../utils/costTargets';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import { hasAccountSetting } from '../../../services/accessManagement/accountSettingsUtils';
import DemoProfile from './DemoProfile';


const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user: authUser, backendUser, accountSettings, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState<BackendUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDemoAutomationModal, setShowDemoAutomationModal] = useState(false);
  const [showDemoExtendModal, setShowDemoExtendModal] = useState(false);
  const [showClearRevenueSelectionModal, setShowClearRevenueSelectionModal] = useState(false);
  const [showClearOrdersSelectionModal, setShowClearOrdersSelectionModal] = useState(false);
  const [showClearRevenueModal, setShowClearRevenueModal] = useState(false);
  const [showClearOrdersModal, setShowClearOrdersModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [selectedRevenueSourceIds, setSelectedRevenueSourceIds] = useState<string[]>([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [selectedRestaurantNames, setSelectedRestaurantNames] = useState<string[]>([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');
  const [emailChangeStatus, setEmailChangeStatus] = useState<'idle' | 'pending' | 'error'>('idle');
  const [reportSettingsModalRestaurantId, setReportSettingsModalRestaurantId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDemoAutomationDone = () => {
    setShowDemoAutomationModal(false);
    navigate('/dashboard');
  };

  // Restaurants, sections, platforms and revenue sources
  const restaurants = useRestaurantStore(s => s.restaurants);
  const restaurantSections = useRestaurantStore(s => s.restaurantSections);
  const restaurantSectionPlatforms = useRestaurantStore(s => s.restaurantSectionPlatforms);
  const restaurantRevenueSources = useRestaurantStore(s => s.restaurantRevenueSources);
  const loadRestaurantPlatformData = useRestaurantStore(s => s.loadAllData);
  // Report settings come from the restaurants load (cached in the store) — no
  // separate per-restaurant fetch. The edit modal writes back to this cache.
  const restaurantReportSettings = useRestaurantStore(s => s.reportSettings);
  const isLoading = restaurants.length === 0;

  // Selected restaurant for demo automation
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant); 

  // fetch user profile
  useEffect(() => {
    if (!authUser) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const data = await fetchUserProfile();
        setProfile(data);
      } catch (err: any) {
        console.error('Error fetching profile', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [authUser, navigate]);

  // Reload restaurant data (incl. cached report settings) when navigating back
  // to profile. forceReload re-fetches /getAll, which carries report_settings.
  useEffect(() => {
    if (authUser && location.pathname === '/profile') {
      loadRestaurantPlatformData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, authUser, loadRestaurantPlatformData]);

  const handleReportSettingsSaved = () => {
    // The modal writes the saved settings straight into the restaurant store
    // cache, so the displayed chips update with no refetch. Nothing to do here.
  };

  if (!authUser || loading || !profile) return null;

  // Check if user can access demo automation features
  const canAccessDemoAutomationFeatures = canAccessDemoAccount(backendUser, accountSettings);
  // Staff may extend demo data; creation and clearing stay admin-only.
  const canExtendDemoData = !!backendUser && accountSettings.includes('demo_account');

  const initial =
    authUser.user_metadata?.firstName?.charAt(0).toUpperCase() ||
    authUser.email?.charAt(0).toUpperCase() ||
    '';

  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error', err);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUserService();
      // The account is gone server-side, but the Supabase session still sits in
      // localStorage — without a local sign-out getSession() restores the deleted
      // user on the next load and routes them into the app with nothing to show.
      await supabaseSignOut().catch(err => console.error('Sign out after account deletion failed', err));
      setUser(null);
      navigate('/login', { replace: true });
    } catch (err: any) {
      console.error('Error deleting account', err);
      setError(err.message);
      setShowDeleteModal(false);
    }
  };

  const handlePriceAlertToggle = async (enabled: boolean) => {
    try {
      await updatePriceAlertPreference(enabled);
      setProfile(prev => prev ? { ...prev, receive_price_alert_emails: enabled } : null);
    } catch (err: any) {
      console.error('Error updating price alert preference', err);
      setError(err.message);
    }
  };

  const handleWeeklyReportsToggle = async (enabled: boolean) => {
    try {
      await updateWeeklyReportsPreference(enabled);
      setProfile(prev => prev ? { ...prev, receive_weekly_reports_emails: enabled } : null);
    } catch (err: any) {
      console.error('Error updating weekly reports preference', err);
      setError(err.message);
    }
  };

  const handleStaffSignupEmailToggle = async (enabled: boolean) => {
    try {
      await updateStaffSignupEmailPreference(enabled);
      setProfile(prev => prev ? { ...prev, receive_staff_signup_emails: enabled } : null);
    } catch (err: any) {
      console.error('Error updating staff signup email preference', err);
      setError(err.message);
    }
  };

  const handleCreateRestaurant = async () => {
    await loadRestaurantPlatformData(true);
    setIsCreateModalOpen(false);
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUpdatingProfile(true);
    try {
      const updatedUser = await updateUserProfile(undefined, file);
      setProfile(updatedUser);
      setError(null);
    } catch (err: any) {
      console.error('Error updating profile picture', err);
      setError(err.message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleNameEdit = () => {
    setEditedName(profile?.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (!editedName.trim()) return;

    setIsUpdatingProfile(true);
    try {
      const updatedUser = await updateUserProfile(editedName);
      setProfile(updatedUser);
      setIsEditingName(false);
      setError(null);
    } catch (err: any) {
      console.error('Error updating name', err);
      setError(err.message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleEmailEdit = () => {
    setEditedEmail(profile?.email || '');
    setIsEditingEmail(true);
    setEmailChangeStatus('idle');
  };

  const handleEmailSave = async () => {
    if (!editedEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsUpdatingProfile(true);
    setEmailChangeStatus('idle');
    try {
      await initiateEmailChange(editedEmail);
      setEmailChangeStatus('pending');
      setIsEditingEmail(false);
      setError(null);
    } catch (err: any) {
      console.error('Error initiating email change', err);
      setError(err.message);
      setEmailChangeStatus('error');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleEmailCancel = () => {
    setIsEditingEmail(false);
    setEditedEmail('');
    setEmailChangeStatus('idle');
  };

  const handleRevenueSelectionConfirm = (ids: string[], restaurantNames: string[]) => {
    setSelectedRevenueSourceIds(ids);
    setSelectedRestaurantNames(restaurantNames);
    setShowClearRevenueSelectionModal(false);
    setShowClearRevenueModal(true);
  };

  const handleOrdersSelectionConfirm = (ids: string[], restaurantNames: string[]) => {
    setSelectedSectionIds(ids);
    setSelectedRestaurantNames(restaurantNames);
    setShowClearOrdersSelectionModal(false);
    setShowClearOrdersModal(true);
  };

  const handleClearRevenue = async () => {
    if (selectedRevenueSourceIds.length === 0) return;

    setIsClearing(true);
    setError(null);
    try {
      const result = await clearRevenueBySourceIds(selectedRevenueSourceIds);
      console.log(`Cleared ${result.deletedCount} revenue entries`);
      setShowClearRevenueModal(false);
      setSelectedRevenueSourceIds([]);
      setSelectedRestaurantNames([]);
      await loadRestaurantPlatformData(true);
    } catch (err: any) {
      console.error('Error clearing revenue:', err);
      setError(err.message);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearOrders = async () => {
    if (selectedSectionIds.length === 0) return;

    setIsClearing(true);
    setError(null);
    try {
      const result = await clearOrdersBySectionIds(selectedSectionIds);
      console.log(`Cleared ${result.deletedCount} order entries`);
      setShowClearOrdersModal(false);
      setSelectedSectionIds([]);
      setSelectedRestaurantNames([]);
      await loadRestaurantPlatformData(true);
    } catch (err: any) {
      console.error('Error clearing orders:', err);
      setError(err.message);
    } finally {
      setIsClearing(false);
    }
  };

  if (backendUser?.demoContext) return <DemoProfile profile={profile} onLogout={handleLogout} />;

  return (
    <main className="min-h-screen text-text dark:text-text-dark">
      <MobilePageHeader title={t('profile.title')} />
      {/* Desktop: Two-column layout, Mobile: Single column */}
      <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-[400px_1fr] lg:gap-6">

        {/* LEFT COLUMN (Desktop): User Info, Preferences, and Actions */}
        <div className="space-y-6 mb-6 lg:mb-0">
          {/* Profile Card */}
          <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-8 space-y-6">
            {/* Avatar with Upload */}
            <div className="relative w-24 h-24 mx-auto group">
              {profile.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-400 dark:bg-gray-500 text-card dark:text-card-dark rounded-full flex items-center justify-center text-2xl font-bold">
                  {initial}
                </div>
              )}
              <button
                onClick={handleProfilePictureClick}
                disabled={isUpdatingProfile}
                className="absolute inset-0 bg-black/50 dark:bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Camera className="w-6 h-6 text-white dark:text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* User Info */}
            {error && <p className="text-destructive dark:text-destructive-dark text-center text-sm">{error}</p>}
            <div className="space-y-4">
              {/* Name field with edit */}
              <div>
                <h3 className="text-sm font-semibold text-muted dark:text-muted-dark">Name</h3>
                {isEditingName ? (
                  <div className="flex gap-2 mt-1">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="flex-1 px-3 py-1 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      placeholder="Enter your name"
                    />
                    <button
                      onClick={handleNameSave}
                      disabled={isUpdatingProfile || !editedName.trim()}
                      className="px-3 py-1 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleNameCancel}
                      disabled={isUpdatingProfile}
                      className="px-3 py-1 border border-zinc-300 dark:border-zinc-700 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-text dark:text-text-dark">{profile.name || 'Not set'}</p>
                    <button
                      onClick={handleNameEdit}
                      className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition"
                    >
                      <Pencil className="w-3 h-3 text-muted dark:text-muted-dark" />
                    </button>
                  </div>
                )}
              </div>

              {/* Email field with edit (admin only) */}
              <div>
                <h3 className="text-sm font-semibold text-muted dark:text-muted-dark">{t('profile.email')}</h3>
                {isEditingEmail ? (
                  <div className="flex gap-2 mt-1">
                    <input
                      type="email"
                      value={editedEmail}
                      onChange={(e) => setEditedEmail(e.target.value)}
                      className="flex-1 px-3 py-1 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      placeholder="Enter your email"
                    />
                    <button
                      onClick={handleEmailSave}
                      disabled={isUpdatingProfile || !editedEmail.trim()}
                      className="px-3 py-1 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleEmailCancel}
                      disabled={isUpdatingProfile}
                      className="px-3 py-1 border border-zinc-300 dark:border-zinc-700 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex-1">
                      <p className="text-text dark:text-text-dark">{profile.email}</p>
                      {emailChangeStatus === 'pending' && (
                        <p className="text-xs text-accent dark:text-accent-dark mt-1">
                          ⏳ Email change pending. Please check both your old and new email for confirmation links.
                        </p>
                      )}
                    </div>
                    {backendUser?.role === 'admin' && (
                      <button
                        onClick={handleEmailEdit}
                        disabled={emailChangeStatus === 'pending'}
                        className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Pencil className="w-3 h-3 text-muted dark:text-muted-dark" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Other fields */}
              {[
                { label: t('profile.userID'), value: profile.id },
                { label: t('profile.subscriptionStatus'), value: profile.subscription_status },
                { label: t('profile.accountCreated'), value: new Date(profile.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) }
              ].map(({ label, value }) => (
                <div key={label}>
                  <h3 className="text-sm font-semibold text-muted dark:text-muted-dark">{label}</h3>
                  <p className="text-text dark:text-text-dark">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {hasAccountSetting('ambassador', backendUser, accountSettings) && (
            <button
              type="button"
              onClick={() => navigate('/profile/referrals')}
              className="group w-full px-6 py-4 flex items-center gap-3 bg-card dark:bg-card-dark text-text dark:text-text-dark rounded-2xl shadow-sm hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:focus-visible:ring-emerald-400"
            >
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 text-muted dark:text-muted-dark">
                <Users className="w-[18px] h-[18px]" aria-hidden />
              </span>
              <span className="flex-1 text-left text-sm font-semibold">{t('ambassador.profileButton')}</span>
              <ChevronRight className="w-4 h-4 shrink-0 text-muted dark:text-muted-dark motion-safe:group-hover:translate-x-0.5 transition-transform" aria-hidden />
            </button>
          )}

          {/* Preferences Card */}
          <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-8 space-y-4">
            <h2 className="text-lg font-semibold text-text dark:text-text-dark">{t('profile.preferences')}</h2>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-text dark:text-text-dark">{t('profile.priceAlertEmails')}</h3>
                <p className="text-xs text-muted dark:text-muted-dark">{t('profile.priceAlertEmailsDescription')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile?.receive_price_alert_emails ?? true}
                  onChange={(e) => handlePriceAlertToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 dark:peer-focus:ring-accent-dark/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-accent dark:peer-checked:bg-accent-dark"></div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-text dark:text-text-dark">{t('profile.weeklyReportsEmails')}</h3>
                <p className="text-xs text-muted dark:text-muted-dark">{t('profile.weeklyReportsEmailsDescription')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile?.receive_weekly_reports_emails ?? false}
                  onChange={(e) => handleWeeklyReportsToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 dark:peer-focus:ring-accent-dark/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-accent dark:peer-checked:bg-accent-dark"></div>
              </label>
            </div>
            {canAccessStaffPage(backendUser) && (
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text dark:text-text-dark">{t('profile.staffSignupEmails')}</h3>
                  <p className="text-xs text-muted dark:text-muted-dark">{t('profile.staffSignupEmailsDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile?.receive_staff_signup_emails ?? (backendUser?.role === 'admin')}
                    onChange={(e) => handleStaffSignupEmailToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 dark:peer-focus:ring-accent-dark/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-accent dark:peer-checked:bg-accent-dark"></div>
                </label>
              </div>
            )}
          </div>

          {/* Actions (Logout & Delete) - Hidden on mobile, shown on desktop in left column */}
          <div className="hidden lg:flex flex-col items-center space-y-4">
            {canAccessDemoAutomationFeatures && (
              <button
                onClick={() => setShowDemoAutomationModal(true)}
                disabled={!selectedRestaurant?.id}
                className="w-full max-w-xs px-4 py-2 bg-accent dark:bg-accent-dark hover:opacity-90 text-white dark:text-white rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4" />
                Demo Automation
              </button>
            )}
            {canExtendDemoData && (
              <button
                onClick={() => setShowDemoExtendModal(true)}
                disabled={!selectedRestaurant?.id}
                className="w-full max-w-xs px-4 py-2 border border-accent dark:border-accent-dark text-accent dark:text-accent-dark hover:bg-accent/10 dark:hover:bg-accent-dark/10 rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CalendarClock className="w-4 h-4" />
                {t('profile.demoExtend.buttonLabel')}
              </button>
            )}
            {canAccessDemoAutomationFeatures && (
              <>
                <button
                  onClick={() => setShowClearRevenueSelectionModal(true)}
                  disabled={isClearing}
                  className="w-full max-w-xs px-4 py-2 bg-orange-500 dark:bg-orange-600 hover:bg-orange-600 dark:hover:bg-orange-700 text-white dark:text-white rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Revenue Data
                </button>
                <button
                  onClick={() => setShowClearOrdersSelectionModal(true)}
                  disabled={isClearing}
                  className="w-full max-w-xs px-4 py-2 bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Order Data
                </button>
              </>
            )}
            <button
              onClick={handleLogout}
              className="w-full max-w-xs px-4 py-2 bg-gray-500 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-600 text-white dark:text-white rounded-2xl transition"
            >
              {t('profile.logout')}
            </button>
            {backendUser?.role === 'admin' && (
            <button
              onClick={handleDeleteClick}
              className="w-full max-w-xs px-4 py-2 bg-destructive dark:bg-destructive-dark hover:bg-destructive-light dark:hover:bg-destructive text-white dark:text-white rounded-2xl transition"
            >
              {t('profile.deleteAccount')}
              </button>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN (Desktop) / Middle Section (Mobile): Restaurants with Suppliers and Revenue Sources */}
        <div className="mb-6 lg:mb-0">
          <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-8 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-text dark:text-text-dark">{t('profile.yourRestaurants')}</h2>
              {backendUser?.role === 'admin' && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('profile.createRestaurant')}</span>
                </button>
              )}
            </div>

            {isLoading ? (
              <p>{t('profile.loadingRestaurants')}</p>
            ) : restaurants.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {[...restaurants].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).map(r => {
                  const sections = restaurantSections[r.id] || [];
                  // One group per section; a supplier configured twice in a section shows once
                  const platformsBySection = sections
                    .map(section => {
                      const sorted = [...(restaurantSectionPlatforms[section.id] || [])]
                        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                      const platforms = sorted.filter((p, i) => sorted.findIndex(o => o.platformName === p.platformName) === i);
                      return { section, platforms };
                    })
                    .filter(g => g.platforms.length > 0);
                  const allRevenueSources = (restaurantRevenueSources[r.id] || [])
                    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

                  return (
                    <div key={r.id} className="border border-zinc-200 dark:border-zinc-700 rounded-2xl p-6 bg-background dark:bg-background-dark hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <RestaurantLogo logoUrl={r.logoUrl} name={r.name} size="small" />
                          <h3 className="font-semibold text-lg text-text dark:text-text-dark">{r.name}</h3>
                        </div>
                        {backendUser?.role === 'admin' && (
                          <button
                            onClick={() => navigate(`/restaurant/${r.id}`)}
                            className="flex-shrink-0 p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                            aria-label={t('profile.editRestaurant')}
                          >
                            <Pencil className="w-4 h-4 text-muted dark:text-muted-dark" />
                          </button>
                        )}
                      </div>

                      {/* Revenue sources on top, suppliers below in two columns to save height */}
                      <div className="space-y-4">
                        {/* Revenue Sources */}
                        <div>
                          <h4 className="text-sm font-medium text-muted dark:text-muted-dark mb-2">{t('profile.revenueSources')}</h4>
                          {allRevenueSources.length > 0 ? (
                            <div className="space-y-2">
                              {allRevenueSources.map(revenueSource => (
                                <div key={revenueSource.id} className="flex items-center gap-2 text-sm">
                                  <span className="text-text dark:text-text-dark">{revenueSource.revenueSourceName}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted dark:text-muted-dark">{t('profile.noRevenueSources')}</p>
                          )}
                        </div>

                        {/* Suppliers */}
                        <div>
                          <h4 className="text-sm font-medium text-muted dark:text-muted-dark mb-2">{t('profile.suppliers')}</h4>
                          {platformsBySection.length > 0 ? (
                            <div className="space-y-3">
                              {platformsBySection.map(({ section, platforms }) => (
                                <div key={section.id}>
                                  <p className="text-xs font-medium uppercase tracking-wide text-muted dark:text-muted-dark mb-1.5 truncate">{section.name}</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                                    {platforms.map(platform => (
                                      <div key={platform.id} className="flex items-center gap-2 text-sm min-w-0">
                                        <div className="w-10 h-6 flex items-center justify-center flex-shrink-0">
                                          <SupplierLogo platform={Platform.fromString(platform.platformName)} size="small" className="max-w-full max-h-full" />
                                        </div>
                                        <span className="text-text dark:text-text-dark truncate">{formatPlatformName(platform.platformName)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted dark:text-muted-dark">{t('profile.noSuppliers')}</p>
                          )}
                        </div>
                      </div>

                      {/* Add Data Source Button */}
                      {backendUser?.role === 'admin' && (
                        <button
                          onClick={() => navigate(`/profile/add-data-source/${r.id}`)}
                          className="mt-4 w-full rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 hover:border-emerald-500 dark:hover:border-emerald-500 transition-all py-3 flex items-center justify-center gap-2 text-sm text-muted dark:text-muted-dark hover:text-emerald-600 dark:hover:text-emerald-400"
                        >
                          <Plus className="w-4 h-4" />
                          {t('wizard.addDataSource')}
                        </button>
                      )}

                      {/* Restaurant Settings */}
                      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-muted dark:text-muted-dark">
                            {t('profile.restaurantSettings.title')}
                          </h4>
                          {backendUser?.role === 'admin' && (
                            <button
                              onClick={() => setReportSettingsModalRestaurantId(r.id)}
                              className="flex-shrink-0 p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                              aria-label={t('profile.restaurantSettings.title')}
                            >
                              <Pencil className="w-3.5 h-3.5 text-muted dark:text-muted-dark" />
                            </button>
                          )}
                        </div>
                        {restaurantReportSettings[r.id] ? (
                          <div className="space-y-2">
                            {restaurantReportSettings[r.id].trackedDishes.food.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted dark:text-muted-dark mb-1">Food Dishes ({restaurantReportSettings[r.id].trackedDishes.food.length})</p>
                                <div className="flex flex-wrap gap-1">
                                  {restaurantReportSettings[r.id].trackedDishes.food.slice(0, 3).map((dish, idx) => (
                                    <span key={idx} className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      {dish}
                                    </span>
                                  ))}
                                  {restaurantReportSettings[r.id].trackedDishes.food.length > 3 && (
                                    <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      +{restaurantReportSettings[r.id].trackedDishes.food.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {restaurantReportSettings[r.id].trackedDishes.drinks.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted dark:text-muted-dark mb-1">Drinks Dishes ({restaurantReportSettings[r.id].trackedDishes.drinks.length})</p>
                                <div className="flex flex-wrap gap-1">
                                  {restaurantReportSettings[r.id].trackedDishes.drinks.slice(0, 3).map((dish, idx) => (
                                    <span key={idx} className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      {dish}
                                    </span>
                                  ))}
                                  {restaurantReportSettings[r.id].trackedDishes.drinks.length > 3 && (
                                    <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      +{restaurantReportSettings[r.id].trackedDishes.drinks.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {(() => {
                              const costTargets = resolveCostTargets(restaurantReportSettings[r.id].costTargets);
                              return (
                                <div>
                                  <p className="text-xs font-medium text-muted dark:text-muted-dark mb-1">{t('profile.reportSettings.costTargets.title')}</p>
                                  <div className="flex flex-wrap gap-1">
                                    <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      {t('profile.reportSettings.costTargets.overallShort')} {costTargets.overall}%
                                    </span>
                                    <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      {t('profile.reportSettings.costTargets.foodShort')} {costTargets.food}%
                                    </span>
                                    <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      {t('profile.reportSettings.costTargets.drinksShort')} {costTargets.drinks}%
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                            {(() => {
                              const wastePercentage = resolveWastePercentage(restaurantReportSettings[r.id].wastePercentage);
                              return (
                                <div>
                                  <p className="text-xs font-medium text-muted dark:text-muted-dark mb-1">{t('profile.reportSettings.waste.title')}</p>
                                  <div className="flex flex-wrap gap-1">
                                    <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                      {t('profile.reportSettings.waste.label')} {wastePercentage}%
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                            <div>
                              <p className="text-xs font-medium text-muted dark:text-muted-dark mb-1">{t('profile.reportSettings.sendDay.title')}</p>
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-700 rounded text-text dark:text-text-dark">
                                  {t(`common.weekdays.${restaurantReportSettings[r.id].weeklyReportSendDay ?? 'monday'}`)}
                                </span>
                              </div>
                            </div>
                            {restaurantReportSettings[r.id].trackedDishes.food.length === 0 &&
                             restaurantReportSettings[r.id].trackedDishes.drinks.length === 0 && (
                              <p className="text-xs text-muted dark:text-muted-dark italic">
                                {t('profile.reportSettings.noDishesTracked')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-muted dark:text-muted-dark italic">
                            {t('profile.reportSettings.loadingRestaurantSettings')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted dark:text-muted-dark">
                {t('profile.noRestaurants')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Actions (Logout & Delete) - Shown only on mobile, at the bottom */}
      <div className="lg:hidden max-w-7xl mx-auto mt-6 flex flex-col items-center space-y-4">
        {canAccessDemoAutomationFeatures && (
          <button
            onClick={() => setShowDemoAutomationModal(true)}
            disabled={!selectedRestaurant?.id}
            className="w-full max-w-xs px-4 py-2 bg-accent dark:bg-accent-dark hover:opacity-90 text-white dark:text-white rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Sparkles className="w-4 h-4" />
            Demo Automation
          </button>
        )}
        {canExtendDemoData && (
          <button
            onClick={() => setShowDemoExtendModal(true)}
            disabled={!selectedRestaurant?.id}
            className="w-full max-w-xs px-4 py-2 border border-accent dark:border-accent-dark text-accent dark:text-accent-dark hover:bg-accent/10 dark:hover:bg-accent-dark/10 rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CalendarClock className="w-4 h-4" />
            {t('profile.demoExtend.buttonLabel')}
          </button>
        )}
        {canAccessDemoAutomationFeatures && (
          <>
            <button
              onClick={() => setShowClearRevenueSelectionModal(true)}
              disabled={isClearing}
              className="w-full max-w-xs px-4 py-2 bg-orange-500 dark:bg-orange-600 hover:bg-orange-600 dark:hover:bg-orange-700 text-white dark:text-white rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Clear Revenue Data
            </button>
            <button
              onClick={() => setShowClearOrdersSelectionModal(true)}
              disabled={isClearing}
              className="w-full max-w-xs px-4 py-2 bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Clear Order Data
            </button>
          </>
        )}
        <button
          onClick={handleLogout}
          className="w-full max-w-xs px-4 py-2 bg-gray-500 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-600 text-white dark:text-white rounded-2xl transition"
        >
          {t('profile.logout')}
        </button>
        {backendUser?.role === 'admin' && (
        <button
          onClick={handleDeleteClick}
          className="w-full max-w-xs px-4 py-2 bg-destructive dark:bg-destructive-dark hover:bg-destructive-light dark:hover:bg-destructive text-white dark:text-white rounded-2xl transition"
        >
          {t('profile.deleteAccount')}
          </button>
        )}
      </div>

      {/* Create Restaurant Modal */}
      <SaveRestaurantModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        mode="create"
        onSave={handleCreateRestaurant}
      />

      {/* Report Settings Modal */}
      {reportSettingsModalRestaurantId && (
        <ReportSettingsModal
          isOpen={!!reportSettingsModalRestaurantId}
          onClose={() => setReportSettingsModalRestaurantId(null)}
          restaurantId={reportSettingsModalRestaurantId}
          onSaved={handleReportSettingsSaved}
        />
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={() => setShowDeleteModal(false)}>
          <div
            className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
                  Delete Account?
                </h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>

              <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border border-red-100 dark:border-red-900/30">
                <div className="flex gap-3">
                  <div className="flex-none mt-0.5">
                    <div className="h-8 w-8 rounded-lg bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-500 grid place-items-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-900 dark:text-zinc-100 font-medium mb-1">This action cannot be undone</p>
                    <p className="text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed">
                      {t('profile.deleteAccountConfirmation')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-700 text-white dark:text-white hover:bg-red-700 transition"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Automation Modal */}
      {showDemoAutomationModal && selectedRestaurant?.id && (
        <DemoAutomationModal
          restaurantId={selectedRestaurant.id}
          onDone= {() => handleDemoAutomationDone()}
          onClose={() => setShowDemoAutomationModal(false)}
        />
      )}

      {/* Demo Data: Fill To Today */}
      <DemoExtendModal
        isOpen={canExtendDemoData && showDemoExtendModal && !!selectedRestaurant?.id}
        restaurantId={selectedRestaurant?.id || ''}
        onClose={() => setShowDemoExtendModal(false)}
      />

      {/* Clear Revenue Selection Modal */}
      <ClearDataSelectionModal
        isOpen={showClearRevenueSelectionModal}
        onClose={() => setShowClearRevenueSelectionModal(false)}
        onConfirm={handleRevenueSelectionConfirm}
        type="revenue"
        restaurants={restaurants}
        restaurantSections={restaurantSections}
        restaurantRevenueSources={restaurantRevenueSources}
      />

      {/* Clear Orders Selection Modal */}
      <ClearDataSelectionModal
        isOpen={showClearOrdersSelectionModal}
        onClose={() => setShowClearOrdersSelectionModal(false)}
        onConfirm={handleOrdersSelectionConfirm}
        type="orders"
        restaurants={restaurants}
        restaurantSections={restaurantSections}
        restaurantRevenueSources={restaurantRevenueSources}
      />

      {/* Clear Revenue Confirmation Modal */}
      <ClearDataModal
        isOpen={showClearRevenueModal}
        onClose={() => {
          setShowClearRevenueModal(false);
          setSelectedRevenueSourceIds([]);
          setSelectedRestaurantNames([]);
        }}
        onConfirm={handleClearRevenue}
        isClearing={isClearing}
        restaurantName={selectedRestaurantNames.join(', ')}
        type="revenue"
      />

      {/* Clear Orders Confirmation Modal */}
      <ClearDataModal
        isOpen={showClearOrdersModal}
        onClose={() => {
          setShowClearOrdersModal(false);
          setSelectedSectionIds([]);
          setSelectedRestaurantNames([]);
        }}
        onConfirm={handleClearOrders}
        isClearing={isClearing}
        restaurantName={selectedRestaurantNames.join(', ')}
        type="orders"
      />

    </main>
  );
};

export default ProfilePage;
